import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Globe, Shield, Lock, Zap, Check, X, Sparkles, Wifi, IndianRupee,
  Headphones, Link2, BadgeCheck, ShieldCheck, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHead from "@/components/SiteHead";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { LandingHeader, LandingFooter, landingLogoVars, yearlyPlans, rupee } from "@/components/LandingChrome";
import api, { extractErr } from "@/lib/api";

const FALLBACK_TLDS = [
  { tld: "com", price: 999, renew: 999, tag: "Sabse trusted", blurb: "Best for B2C brands worldwide.", featured: true },
  { tld: "in", price: 599, renew: 599, tag: "India favourite", blurb: "Desi identity. Perfect for Indian shops.", featured: true },
  { tld: "co.in", price: 499, renew: 499, tag: "Sasta & best", blurb: "Budget pick. Fast setup, same DNS.", featured: true },
  { tld: "store", price: 799, renew: 799, tag: "Shop ready", blurb: "Sounds like a dukaan from day one.", featured: true },
  { tld: "shop", price: 699, renew: 699, tag: "Retail", blurb: "Short, clear, made for selling." },
  { tld: "online", price: 599, renew: 599, tag: "Modern", blurb: "Great when the .com is taken." },
  { tld: "co", price: 899, renew: 899, tag: "Startup", blurb: "Short brandable alternative to .com." },
  { tld: "net", price: 799, renew: 799, tag: "Classic", blurb: "Trusted fallback for tech & services." },
];

function badgeClass(b) {
  if (b === "TAKEN") return "bg-stone-200 text-stone-600";
  if (b === "AVAILABLE") return "bg-emerald-100 text-emerald-800";
  return "bg-orange-100 text-orange-800";
}

function ResultRow({ row, onPick }) {
  const letter = (row.sld || row.domain || "d")[0].toUpperCase();
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 sm:px-5 py-4 border-b border-black/5 last:border-0" data-testid={`domain-row-${row.domain}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className="w-10 h-10 rounded-full bg-stone-100 text-stone-800 font-bold flex items-center justify-center shrink-0">{letter}</span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-stone-900 truncate">{row.domain}</span>
            {(row.badges || []).map((b) => (
              <span key={b} className={`text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full ${badgeClass(b)}`}>{b}</span>
            ))}
            {row.source === "ai" ? <span className="text-[10px] font-bold text-[#FF6B00] inline-flex items-center gap-0.5"><Sparkles className="w-3 h-3" /> AI</span> : null}
          </div>
          <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-stone-500">
            {(row.features || ["Free SSL", "Auto-connect", "Privacy"]).map((f) => (
              <span key={f} className="inline-flex items-center gap-1"><Shield className="w-3 h-3" /> {f}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-4 shrink-0">
        <div className="text-right">
          <div className="font-bold text-stone-900">{rupee(row.price)}<span className="text-xs font-normal text-stone-500"> first year</span></div>
          <div className="text-[11px] text-stone-500">Renew {rupee(row.renew_price ?? row.price)}/yr</div>
        </div>
        {row.available ? (
          <Button type="button" onClick={() => onPick(row)} data-testid={`domain-add-${row.domain}`} className="rounded-full bg-stone-900 hover:bg-stone-800 text-white font-semibold px-4">
            Add to cart +
          </Button>
        ) : (
          <Button type="button" disabled className="rounded-full bg-stone-200 text-stone-500 font-semibold px-4">Taken</Button>
        )}
      </div>
    </div>
  );
}

function SearchSkeleton() {
  return (
    <section id="domain-results" className="max-w-[920px] mx-auto px-5 lg:px-8 pb-16" data-testid="domain-search-skeleton">
      <div className="rounded-[24px] bg-white border border-black/10 shadow-sm overflow-hidden">
        <div className="px-5 py-4 flex items-center gap-2 text-sm text-stone-600 border-b border-black/5">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2ED4A6] opacity-70" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#2ED4A6]" />
          </span>
          <Loader2 className="w-4 h-4 animate-spin" />
          Checking availability and live prices…
        </div>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-3 px-4 sm:px-5 py-4 border-b border-black/5 last:border-0">
            <div className="w-10 h-10 rounded-full bg-stone-100 animate-pulse shrink-0" />
            <div className="flex-1 space-y-2 min-w-0">
              <div className="h-4 w-44 max-w-[60%] rounded-full bg-stone-100 animate-pulse" />
              <div className="h-3 w-56 max-w-[80%] rounded-full bg-stone-50 animate-pulse" />
            </div>
            <div className="h-9 w-24 rounded-full bg-stone-100 animate-pulse shrink-0" />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function DomainPage() {
  const nav = useNavigate();
  const cfg = useSiteConfig();
  const plans = yearlyPlans(cfg.pricing?.plans);
  const websitePrice = plans.find((p) => p.id === "website")?.price || 1999;
  const logoVars = landingLogoVars(cfg);

  const [catalog, setCatalog] = useState([]);
  const [minPrice, setMinPrice] = useState(null);
  const [pricesReady, setPricesReady] = useState(false);
  const [pricesLive, setPricesLive] = useState(false);
  const [query, setQuery] = useState("");
  const [tld, setTld] = useState("com");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [filter, setFilter] = useState("all");
  const [pick, setPick] = useState(null);
  const [footerQ, setFooterQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    let liveApplied = false;
    const applyCatalog = (data, live) => {
      if (!live && liveApplied) return;
      const next = Array.isArray(data.tlds) && data.tlds.length ? data.tlds : FALLBACK_TLDS;
      setCatalog(next);
      setMinPrice(data.min_price || Math.min(...next.map((t) => Number(t.price) || 0).filter(Boolean)) || 499);
      setPricesReady(true);
      if (live) {
        liveApplied = true;
        setPricesLive(true);
      }
    };
    api.get("/domains/config").then(({ data }) => {
      if (!cancelled) applyCatalog(data, false);
    }).catch(() => {
      if (cancelled) return;
      setCatalog(FALLBACK_TLDS);
      setMinPrice(499);
      setPricesReady(true);
    });
    api.get("/domains/config/live").then(({ data }) => {
      if (!cancelled && Array.isArray(data.tlds) && data.tlds.length) applyCatalog(data, true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const featured = catalog.filter((t) => t.featured).slice(0, 4);
  const pills = featured.length ? featured : catalog.slice(0, 4);

  const search = async (q, ext) => {
    const name = (q || "").trim();
    if (!name || !pricesReady) return;
    setBusy(true);
    setTimeout(() => document.getElementById("domain-results")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    try {
      const { data } = await api.post("/domains/search", { query: name, tld: ext || tld, suggest: true });
      setResults(data);
      setFilter("all");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBusy(false);
    }
  };

  const rows = useMemo(() => {
    if (!results) return [];
    let list = [...(results.results || [])];
    const sugg = (results.suggestions || []).filter((s) => s.available);
    const merged = [...list];
    sugg.forEach((s) => {
      if (!merged.some((r) => r.domain === s.domain)) merged.push(s);
    });
    if (filter === "available") return merged.filter((r) => r.available);
    if (filter === "in") return merged.filter((r) => r.tld === "in" || r.tld === "co.in");
    if (filter === "com") return merged.filter((r) => r.tld === "com");
    if (filter === "brandable") return merged.filter((r) => r.source === "ai" || (r.available && r.sld && r.sld.length <= 12));
    return merged;
  }, [results, filter]);

  const goSignup = (mode) => {
    if (!pick) return;
    const params = new URLSearchParams({ domain: pick.domain });
    if (mode === "domain_only") params.set("domain_only", "1");
    else params.set("plan", "website");
    nav(`/signup?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[#FFFBF2] text-stone-900" style={logoVars} data-testid="domain-page">
      <SiteHead title="Buy a domain in India | MeraStartup" description="Search, claim and connect your domain in 30 seconds. No GoDaddy drama. From ₹499/year with free SSL and 1-click store connect." path="/domain" />
      <LandingHeader websitePrice={websitePrice} active="domains" />

      <section className="relative overflow-hidden" data-testid="domain-hero">
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: "linear-gradient(rgba(28,25,23,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(28,25,23,0.045) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }} />
        <div className="absolute -left-24 -top-24 w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(255,186,140,0.45)" }} />
        <div className="absolute -right-16 top-20 w-72 h-72 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(167,243,208,0.4)" }} />

        <div className="hidden lg:block absolute left-8 top-28 w-52 rounded-2xl bg-white border border-black/10 shadow-lg p-4 domain-float">
          <div className="text-xs font-bold text-stone-800">Ideas Deserve Domains</div>
          <button type="button" className="mt-3 w-8 h-8 rounded-full bg-[#2ED4A6] text-white flex items-center justify-center" aria-hidden>→</button>
        </div>
        <div className="hidden lg:block absolute left-16 top-[18.5rem] text-[11px] italic text-stone-500 max-w-[10rem] rotate-[-6deg]">Turn your idea into a brand online. ↗</div>

        <div className="hidden lg:flex absolute right-10 top-24 flex-wrap gap-2 w-56 domain-float-slow justify-end">
          {[".in", ".com", ".store", ".co.in", ".shop"].map((x) => (
            <span key={x} className="px-3 py-1 rounded-full bg-white border border-black/10 shadow-sm text-xs font-semibold">{x}</span>
          ))}
        </div>
        <div className="hidden lg:block absolute right-8 bottom-8 w-56 rounded-2xl bg-white border border-black/10 shadow-lg p-4">
          <div className="font-bold text-sm">Same name. Bigger possibilities.</div>
          <p className="mt-2 text-[11px] italic text-stone-500">A bigger brand starts with a domain. ↙</p>
        </div>

        <div className="relative max-w-[920px] mx-auto px-5 lg:px-8 pt-12 sm:pt-16 pb-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/80 px-3 py-1 text-[11px] font-extrabold tracking-[0.16em] text-stone-600">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2ED4A6]" /> DOMAINS FOR BIGGER DREAMS
          </div>
          <h1 className="ms-serif mt-5 font-bold leading-[1.12] text-[2.15rem] sm:text-[2.6rem] lg:text-[2.15rem] xl:text-[2.4rem] lg:whitespace-nowrap" data-testid="domain-hero-headline">
            <span className="block lg:inline text-stone-900">Apna naam.</span>
            {" "}
            <span className="block lg:inline text-[#C45C26]">Apna domain.</span>
            {" "}
            <span className="block lg:inline text-stone-900">Apna haq.</span>
          </h1>
          <p className="mt-4 text-stone-600 max-w-2xl mx-auto" data-testid={pricesReady ? "domain-min-price" : "domain-prices-loading"}>
            {pricesReady && minPrice != null ? (
              <>Search, claim &amp; connect your domain in 30 seconds. No GoDaddy jhanjhat. No dollar drama. Bas {rupee(minPrice)}/year se shuru.</>
            ) : (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                Loading live prices…
              </span>
            )}
          </p>
          <form
            className="mt-8 flex flex-col sm:flex-row sm:items-center gap-2 bg-white border border-black/10 rounded-[28px] sm:rounded-full p-2 shadow-md"
            onSubmit={(e) => { e.preventDefault(); search(query, tld); }}
            data-testid="domain-search-form"
          >
            <div className="flex-1 flex items-center gap-2 min-w-0 px-3 sm:px-4">
              <Search className="w-5 h-5 text-stone-400 shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search your domain"
                disabled={!pricesReady}
                className="flex-1 h-12 bg-transparent outline-none text-base min-w-0 disabled:opacity-60"
                aria-label="Search domain"
                data-testid="domain-search-input"
              />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto sm:shrink-0">
              <select
                value={tld}
                onChange={(e) => setTld(e.target.value)}
                disabled={!pricesReady}
                className="h-12 min-w-[5.5rem] rounded-full border border-black/10 px-3 text-sm bg-stone-50 disabled:opacity-60"
                data-testid="domain-tld-select"
              >
                {pricesReady ? catalog.map((t) => <option key={t.tld} value={t.tld}>.{t.tld}</option>) : <option>Loading…</option>}
              </select>
              <Button type="submit" disabled={!pricesReady || busy} data-testid="domain-search-btn" className="h-12 flex-1 sm:flex-none sm:w-auto rounded-full bg-stone-900 hover:bg-stone-800 text-white font-bold px-6">
                {!pricesReady || busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1.5" /> Search</>}
              </Button>
            </div>
          </form>
          <div className="mt-4 flex flex-wrap justify-center gap-2" data-testid={pricesLive ? "domain-price-pills-live" : pricesReady ? "domain-price-pills" : "domain-price-pills-loading"}>
            {pricesReady ? (
              <>
                {pills.map((t) => (
                  <button
                    key={t.tld}
                    type="button"
                    onClick={() => { setTld(t.tld); if (query) search(query, t.tld); }}
                    className={`px-3 py-1.5 rounded-full text-sm border ${tld === t.tld ? "bg-stone-900 text-white border-stone-900" : "bg-white border-black/10 hover:border-stone-400"}`}
                    data-testid={`domain-pill-${t.tld}`}
                  >
                    .{t.tld} {rupee(t.price)}/yr
                  </button>
                ))}
                <span className="px-3 py-1.5 text-sm text-stone-500">+ {Math.max(catalog.length - 4, 0)} more extensions</span>
              </>
            ) : (
              [1, 2, 3, 4].map((i) => (
                <span key={i} className="h-8 w-28 rounded-full bg-white/80 border border-black/5 animate-pulse" />
              ))
            )}
          </div>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 text-left max-w-3xl mx-auto">
            {[
              { icon: Zap, t: "Instant Activation", d: "Go live in minutes" },
              { icon: ShieldCheck, t: "Secure & Reliable", d: "Trusted providers" },
              { icon: Globe, t: "Free DNS", d: "Manage easily" },
              { icon: Headphones, t: "Dedicated Support", d: "We're here to help" },
            ].map((x) => (
              <div key={x.t} className="flex items-start gap-2.5">
                <span className="w-9 h-9 rounded-full bg-white border border-black/10 flex items-center justify-center shrink-0"><x.icon className="w-4 h-4 text-stone-800" /></span>
                <span>
                  <span className="block text-sm font-bold">{x.t}</span>
                  <span className="text-xs text-stone-500">{x.d}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @keyframes domainFloat { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-10px) } }
          .domain-float { animation: domainFloat 5s ease-in-out infinite; }
          .domain-float-slow { animation: domainFloat 7s ease-in-out infinite; }
        `}</style>
      </section>

      {busy ? <SearchSkeleton /> : results ? (
        <section id="domain-results" className="max-w-[920px] mx-auto px-5 lg:px-8 pb-16" data-testid="domain-results">
          <div className="rounded-[24px] bg-white border border-black/10 shadow-sm overflow-hidden">
            <div className="px-4 sm:px-5 pt-4 flex flex-wrap gap-2">
              {[
                ["all", "All"],
                ["available", "Available"],
                ["in", ".in only"],
                ["com", ".com only"],
                ["brandable", "Short & Brandable"],
              ].map(([id, label]) => (
                <button key={id} type="button" onClick={() => setFilter(id)} data-testid={`domain-filter-${id}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium ${filter === id ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>
                  {label}
                </button>
              ))}
            </div>
            <p className="px-5 pt-3 pb-2 text-sm text-stone-500">Showing {rows.length} domains for #{results.query}</p>
            {rows.map((row) => <ResultRow key={row.domain} row={row} onPick={setPick} />)}
            <div className="px-5 py-3 text-[11px] text-stone-500 bg-stone-50">Instant activation • No paperwork • UPI invoice setup</div>
          </div>
        </section>
      ) : null}

      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 pb-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="ms-serif text-3xl sm:text-4xl font-bold">Har business ke liye ek domain.</h2>
          <p className="mt-3 text-stone-600">Registration and renewal are shown separately. Transparent pricing, no upsell popups.</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {pricesReady ? catalog.slice(0, 8).map((t) => (
            <button key={t.tld} type="button" onClick={() => setTld(t.tld)} className="text-left rounded-[22px] bg-white border border-black/10 p-5 shadow-sm hover:-translate-y-0.5 transition-transform">
              <div className="flex items-start justify-between gap-2">
                <span className="ms-serif text-2xl font-bold">.{t.tld}</span>
                {t.tag ? <span className="text-[10px] font-bold uppercase bg-[#FFEAD1] text-orange-800 px-2 py-0.5 rounded-full">{t.tag}</span> : null}
              </div>
              <div className="mt-3 text-xl font-extrabold">{rupee(t.price)}<span className="text-sm font-normal text-stone-500"> first year</span></div>
              <div className="text-[11px] text-stone-500">Renew {rupee(t.renew ?? t.price)}/yr</div>
              <p className="mt-2 text-sm text-stone-600">{t.blurb}</p>
              <p className="mt-3 text-[11px] text-stone-400">Popular list • Fast setup</p>
            </button>
          )) : [1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-[22px] bg-white border border-black/10 p-5 shadow-sm animate-pulse">
              <div className="h-7 w-16 rounded bg-stone-200" />
              <div className="mt-3 h-6 w-28 rounded bg-stone-200" />
              <div className="mt-2 h-3 w-24 rounded bg-stone-100" />
              <div className="mt-3 h-4 w-full rounded bg-stone-100" />
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#111] text-white">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="ms-serif text-3xl sm:text-4xl font-bold">No upsell drama. Bas kaam ki cheez.</h2>
            <ul className="mt-8 space-y-5">
              {[
                { icon: BadgeCheck, t: "No Upsell Drama", d: "The price you see is the price you pay — every year." },
                { icon: Link2, t: "1-Click Store Connect", d: "Bought with a website? DNS points at your dukaan automatically." },
                { icon: Headphones, t: "Desi Support", d: "Hindi, Hinglish, English. Real humans, not a ticket void." },
              ].map((x) => (
                <li key={x.t} className="flex gap-3">
                  <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><x.icon className="w-5 h-5 text-[#2ED4A6]" /></span>
                  <span><span className="font-bold block">{x.t}</span><span className="text-sm text-white/60">{x.d}</span></span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-[22px] bg-white text-stone-900 overflow-hidden">
            <div className="grid grid-cols-3 text-[11px] font-bold uppercase tracking-wider bg-stone-100 px-4 py-3">
              <span>Feature</span><span>MeraStartup</span><span>Others</span>
            </div>
            {[
              ["Free DNS", true, "₹999/yr extra"],
              ["Free SSL", true, "Paid add-on"],
              ["Renewal same price", true, "Hikes yearly"],
              ["Auto-connect to store", true, "Manual DNS"],
              ["WHOIS privacy", true, "Hidden cost"],
              ["Hindi support", true, "English only"],
              ["UPI + GST invoice", true, "Dollar card"],
            ].map(([f, ok, other]) => (
              <div key={f} className="grid grid-cols-3 px-4 py-3 text-sm border-t border-black/5 items-center">
                <span className="text-stone-700">{f}</span>
                <span className="text-emerald-700 font-semibold inline-flex items-center gap-1"><Check className="w-4 h-4" /> Included</span>
                <span className="text-red-600 inline-flex items-center gap-1"><X className="w-4 h-4" /> {other}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#FF6B00] text-white text-center text-sm font-semibold py-3 px-4">
          Founder ka funda: ₹499 wala .co.in lo, branding .com se karo. Dono secure.
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16">
        <h2 className="ms-serif text-3xl sm:text-4xl font-bold text-center">Sab kuch included. Koi hidden charge nahi.</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: Globe, t: "Free DNS Management", d: "A, CNAME, MX, TXT — change from your dashboard." },
            { icon: Lock, t: "Free SSL", d: "Auto-https with Let’s Encrypt on connected stores." },
            { icon: ShieldCheck, t: "Auto-renew protection", d: "We’ll remind you before expiry. Same price, no trap." },
            { icon: Zap, t: "1-click Store Connect", d: "CNAME www to shops.merastartup.in — same as Settings → Domain. Apex forwards if needed." },
            { icon: Shield, t: "WHOIS Privacy included", d: "Your personal number stays off public WHOIS." },
            { icon: IndianRupee, t: "UPI Invoice for domain", d: "GST invoice + UPI. Pay in rupees, not dollars." },
          ].map((f) => (
            <div key={f.t} className="rounded-[22px] bg-white border border-black/10 p-5 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center"><f.icon className="w-5 h-5" /></div>
              <h3 className="mt-4 font-bold">{f.t}</h3>
              <p className="mt-1.5 text-sm text-stone-600">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[920px] mx-auto px-5 lg:px-8 pb-16">
        <div className="rounded-[28px] bg-[#111] text-white p-8 sm:p-12 text-center">
          <h2 className="ms-serif text-3xl sm:text-4xl font-bold">Claim your naam before someone else does.</h2>
          <form className="mt-6 flex flex-col sm:flex-row gap-3 w-full max-w-xl mx-auto" data-testid="domain-footer-search" onSubmit={(e) => { e.preventDefault(); if (!pricesReady) return; setQuery(footerQ); search(footerQ, tld); }}>
            <input value={footerQ} onChange={(e) => setFooterQ(e.target.value)} placeholder="yourbrand" disabled={!pricesReady} className="w-full sm:flex-1 h-12 rounded-full px-5 text-stone-900 disabled:opacity-60" />
            <Button type="submit" disabled={!pricesReady} className="w-full sm:w-auto h-12 rounded-full bg-white text-stone-900 hover:bg-stone-100 font-bold px-6">Search again</Button>
          </form>
          <Link to="/signup?plan=website" className="mt-3 block w-full max-w-xl mx-auto sm:w-auto">
            <Button className="w-full sm:w-auto rounded-full bg-[#FF6B00] hover:bg-[#e86000] text-white font-bold px-6 h-12">Start selling</Button>
          </Link>
        </div>
      </section>

      <p className="text-center text-xs text-stone-500 pb-6">Domains powered with ❤️ for Indian founders • No dollar drama.</p>
      <LandingFooter />

      {pick ? (
        <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-5" data-testid="domain-confirm-modal">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
            <div className="text-xs font-bold uppercase tracking-widest text-stone-400">Confirm domain</div>
            <h3 className="ms-serif mt-1 text-2xl font-bold">{pick.domain}</h3>
            <p className="mt-1 text-stone-600">
              {rupee(pick.price)} first year · renew {rupee(pick.renew_price ?? pick.price)}/yr
              {pick.premium ? " · premium" : ""} • Free SSL • Privacy included
            </p>
            <div className="mt-6 grid gap-3">
              <Button type="button" onClick={() => goSignup("website")} data-testid="domain-choose-website" className="h-12 rounded-full bg-stone-900 hover:bg-stone-800 text-white font-bold">
                Build my website also
              </Button>
              <Button type="button" variant="outline" onClick={() => goSignup("domain_only")} data-testid="domain-choose-only" className="h-12 rounded-full border-stone-300 font-bold">
                Continue with domain only
              </Button>
            </div>
            <button type="button" className="mt-4 w-full text-sm text-stone-500" onClick={() => setPick(null)}>Keep searching</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
